const API_URL = process.env.REACT_APP_API_URL || 'https://api.tcf-studio.dev';

const getAuthToken = () => localStorage.getItem('authToken');

const getHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// ── User endpoints ──────────────────────────────────────────────

export const getBalance = async () => {
  const res = await fetch(`${API_URL}/billing/balance`, { headers: getHeaders() });
  return res.json();
};

export const getTransactions = async ({ limit = 50, offset = 0, type = null } = {}) => {
  const params = new URLSearchParams({ limit, offset });
  if (type) params.set('type', type);
  const res = await fetch(`${API_URL}/billing/transactions?${params}`, { headers: getHeaders() });
  return res.json();
};

export const createCheckoutSession = async (amountCents) => {
  // Send the origin we are actually on, so Stripe returns the user here.
  // The session token lives in localStorage, which is scoped per origin: a
  // return to a different hostname of ours hands them an empty store and a
  // login screen. The Worker validates this against our own origins and falls
  // back to FRONTEND_URL, so a tampered value cannot redirect anywhere else.
  const origin = window.location.origin;
  const res = await fetch(`${API_URL}/billing/checkout`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      amount_cents: amountCents,
      success_url: `${origin}?page=payment&success=true`,
      cancel_url: `${origin}?page=payment&canceled=true`,
    }),
  });
  return res.json();
};

// ── Admin endpoints ─────────────────────────────────────────────

export const adminGetAllBalances = async () => {
  const res = await fetch(`${API_URL}/billing/admin/balances`, { headers: getHeaders() });
  return res.json();
};

export const adminGetAllTransactions = async ({ limit = 100, offset = 0, type = null, userId = null } = {}) => {
  const params = new URLSearchParams({ limit, offset });
  if (type) params.set('type', type);
  if (userId) params.set('user_id', userId);
  const res = await fetch(`${API_URL}/billing/admin/transactions?${params}`, { headers: getHeaders() });
  return res.json();
};

export const adminGetRevenueSummary = async ({ startDate, endDate } = {}) => {
  const params = new URLSearchParams();
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  const res = await fetch(`${API_URL}/billing/admin/revenue?${params}`, { headers: getHeaders() });
  return res.json();
};

export const adminGetStripeBalance = async () => {
  const res = await fetch(`${API_URL}/billing/admin/stripe/balance`, { headers: getHeaders() });
  return res.json();
};

export const adminListPayouts = async ({ limit = 20 } = {}) => {
  const params = new URLSearchParams({ limit });
  const res = await fetch(`${API_URL}/billing/admin/stripe/payouts?${params}`, { headers: getHeaders() });
  return res.json();
};

export const adminCreatePayout = async (amountCents, currency = 'usd') => {
  const res = await fetch(`${API_URL}/billing/admin/stripe/payouts`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ amount_cents: amountCents, currency }),
  });
  return res.json();
};

export const adminGrantCredits = async (userId, amountCents, description) => {
  const res = await fetch(`${API_URL}/billing/admin/grant`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ user_id: userId, amount_cents: amountCents, description }),
  });
  return res.json();
};

// ── Billing settings ─────────────────────────────────────────────

export const adminGetSettings = async () => {
  const res = await fetch(`${API_URL}/billing/admin/settings`, { headers: getHeaders() });
  return res.json();
};

export const adminUpdateSettings = async (settings) => {
  const res = await fetch(`${API_URL}/billing/admin/settings`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(settings),
  });
  return res.json();
};

// ── AWS promotional credit ───────────────────────────────────────

export const adminGetAwsCredits = async () => {
  const res = await fetch(`${API_URL}/billing/admin/aws-credits`, { headers: getHeaders() });
  return res.json();
};

// ── Provider account balances ────────────────────────────────────

/**
 * Money left at Vast and RunPod. A depleted provider account evicts running
 * clusters with no clear signal, so this is the number that explains an
 * otherwise inexplicable "exited".
 *
 * Always resolves with a `balances` array; a provider that could not be
 * reached carries an `error` instead of a figure.
 */
export const adminGetProviderBalances = async () => {
  const res = await fetch(`${API_URL}/billing/admin/provider-balances`, { headers: getHeaders() });
  return res.json();
};

// ── Cluster costs ────────────────────────────────────────────────

export const adminGetClusterCosts = async ({ userId, startDate, endDate } = {}) => {
  const params = new URLSearchParams();
  if (userId) params.set('user_id', userId);
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  const res = await fetch(`${API_URL}/billing/admin/cluster-costs?${params}`, { headers: getHeaders() });
  return res.json();
};
